import { useRef, useState, type ChangeEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { canEditCustoms } from '@crm/shared'
import { useAuthStore } from '../../auth/authStore'
import { apiErrorText } from '../../../shared/api/errorText'
import { toast } from '../../../shared/ui/toastStore'
import { uploadBrokerFile, type BrokerUploadOutcome } from './brokerActions'
import { BrokerUploadResultModal } from './BrokerUploadResultModal'

const DEFAULT_CLASS =
  'px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50 transition-colors text-gray-700 disabled:opacity-50'

interface Props {
  /** Запрос, в который раскладывать ответ, если в файле нет своего кода запроса. */
  brokerRequestId?: string
  className?: string
  label?: string
}

/**
 * «Загрузить ответ брокера»: Excel → строки → сервер раскладывает ответ на все
 * позиции партномера. Кнопка только у логиста — остальным не рендерится.
 */
export function BrokerUploadButton({ brokerRequestId, className, label }: Props) {
  const role = useAuthStore((s) => s.user?.role)
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  // Итог — окном, а не всплывающей подсказкой: ненайденные артикулы не теряются молча.
  const [outcome, setOutcome] = useState<BrokerUploadOutcome | null>(null)

  if (!canEditCustoms(role)) return null

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    // Сбрасываем input сразу — иначе повторный выбор того же файла не сработает.
    e.target.value = ''
    if (!file) return
    setBusy(true)
    try {
      setOutcome(await uploadBrokerFile(file, brokerRequestId))
      void queryClient.invalidateQueries({ queryKey: ['customs'] })
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось загрузить ответ брокера'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className={className ?? DEFAULT_CLASS}
        title="Excel-ответ брокера: колонки ищутся по заголовкам (Артикул, Код ТНВД, Нетарифка, Сертификат, Особенности, Сбор, Пошлина)"
      >
        {busy ? 'Загрузка…' : (label ?? 'Загрузить ответ брокера')}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        data-testid="broker-upload-input"
        onChange={(e) => void onFile(e)}
      />
      {outcome && <BrokerUploadResultModal outcome={outcome} onClose={() => setOutcome(null)} />}
    </>
  )
}
